import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import DashboardLayout from '../../components/dashboard/DashboardLayout'
import PageHeader from '../../components/dashboard/PageHeader'
import Card from '../../components/dashboard/Card'

export default function AssignDepartmentLeader() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [loadingDepts, setLoadingDepts] = useState(true)
  const [departments, setDepartments] = useState([])
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    city: '',
    state: '',
    country: '',
    post_code: '',
    referral_source: '',
    department_id: '',
    password: ''
  })

  useEffect(() => {
    loadDepartments()
  }, [])

  const loadDepartments = async () => {
    setLoadingDepts(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      const { data: facultiesData, error: facultiesError } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 1)

      if (facultiesError) throw facultiesError

      const { data: deptsData, error: deptsError } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 2)
        .order('name')

      if (deptsError) throw deptsError

      const deptsWithFaculty = deptsData.map(dept => {
        const faculty = facultiesData.find(f => f.id === dept.parent_id)
        return {
          ...dept,
          facultyName: faculty ? faculty.name : 'No Faculty'
        }
      })

      setDepartments(deptsWithFaculty)
    } catch (error) {
      console.error('Load departments error:', error)
      setError('Failed to load departments')
    } finally {
      setLoadingDepts(false)
    }
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser()
      const { data: currentUserData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', currentUser.id)
        .single()

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      })

      if (authError) throw authError

      const qrSignature = authData.user.id + '-' + Date.now()

      // Everyone is a member, Group Leads get QR codes
      const { error: userError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          org_id: currentUserData.org_id,
          email: formData.email,
          phone: formData.phone,
          name: formData.name,
          role: 'member',
          leadership_level: 'group',
          date_of_birth: formData.date_of_birth,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          post_code: formData.post_code,
          referral_source: formData.referral_source,
          qr_signature: qrSignature,
          qr_generated_at: new Date().toISOString(),
          status: 'active'
        })

      if (userError) throw userError

      await supabase
        .from('group_members')
        .insert({
          user_id: authData.user.id,
          group_id: formData.department_id,
          is_leader: true
        })

      alert('Group Lead assigned successfully with QR code')
      navigate('/admin/members')
    } catch (error) {
      console.error('Assign group lead error:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardLayout>
      <PageHeader title="Assign Group Lead" description="Create a new member with group leadership">
        <Link
          to="/admin/members"
          className="inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back
        </Link>
      </PageHeader>

      <div className="p-6">
        <div className="max-w-3xl">
          <Card>
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded mb-6 text-sm">
                {error}
              </div>
            )}

            {loadingDepts ? (
              <div className="text-center py-8">Loading groups...</div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Date of Birth *</label>
                    <input
                      type="date"
                      name="date_of_birth"
                      required
                      value={formData.date_of_birth}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Temporary Password *</label>
                    <input
                      type="password"
                      name="password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="They can change this later"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Street Address *</label>
                  <input
                    type="text"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">City *</label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">State *</label>
                    <input
                      type="text"
                      name="state"
                      required
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Country *</label>
                    <input
                      type="text"
                      name="country"
                      required
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Post Code *</label>
                    <input
                      type="text"
                      name="post_code"
                      required
                      value={formData.post_code}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">How did they hear about us? *</label>
                  <select
                    name="referral_source"
                    required
                    value={formData.referral_source}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select...</option>
                    <option value="word_of_mouth">Word of mouth</option>
                    <option value="social_media">Social media</option>
                    <option value="website">Website</option>
                    <option value="friend_family">Friend/Family</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Assign to Group *</label>
                  <select
                    name="department_id"
                    required
                    value={formData.department_id}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select Group...</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name} ({dept.facultyName})
                      </option>
                    ))}
                  </select>
                  {departments.length === 0 && (
                    <p className="text-sm text-red-500 mt-1">
                      No groups found. Please create groups first in Setup.
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    They will get a QR code and manage this group
                  </p>
                </div>

                <div className="flex gap-4">
                  <Link
                    to="/admin/members"
                    className="flex-1 px-4 py-2 border text-center rounded-md"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    disabled={loading || departments.length === 0}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Assigning...' : 'Assign Group Lead'}
                  </button>
                </div>
              </form>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  )
}